import React, { useState } from 'react';
import {
  PreviewRegularIcon,
  ContextPasteRegularIcon,
  CopyRegularIcon,
  DeleteRegularIcon,
  EditRegularIcon,
  LinkRegularIcon,
  HiddenRegularIcon,
} from 'src/monet/icons';
import { ContextMenuButton } from './components/context-menu-button';
import { useEditorStore } from './editor-store';
import { Section } from './types';
import { useClipboard } from 'src/utils/use-clipboard.hook';
import { generateJourneyShareUrl } from 'src/utils/journey';
import { useEditorContext } from 'src/store/editor';
import { useCurrentOrganization } from 'src/store/organization';
import { shallow } from 'zustand/shallow';
import { useClipboardActions } from './utils/clipboard-actions.hook';
import { useClipboardStore } from './utils/clipboard.store';

type Props = {
  section: Section;
  sectionIds: string[];
  renameAction: () => void;
  friendlyPath: string;
  items?: string[];
};

export const SectionCardContextMenuContent = ({ section, sectionIds, renameAction, items, friendlyPath }: Props) => {
  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);
  const clearContextMenuState = useEditorStore((state) => state.clearContextMenuState);
  const setCurrentSectionId = useEditorStore((state) => state.setCurrentSectionId);

  const friendlyPathFromProps = friendlyPath;

  const [isCurrentSectionHidden, setIsCurrentSectionHidden] = useState(section.hidden);

  const contextMenuState = useEditorStore((state) => state.contextMenuState, shallow);
  const clipboardData = useClipboardStore((state) => state.clipboardData, shallow);

  const journey = useEditorContext((state) => state.journey);
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization, shallow);
  const sectionsHidden = useEditorStore((state) => state.sectionsHidden);
  const setSectionHidden = useEditorStore((state) => state.setSectionHidden);
  const sections = useEditorStore((state) => state.layout.sections);

  const enablePasteForSection = useClipboardStore((state) => state.enablePasteForSection, shallow);
  const clipboardDataContent = useClipboardStore((state) => state.clipboardData, shallow);
  const { copySectionBySectionId, copyFolder } = useClipboardActions();

  const isSingle = items && items?.length === 1;
  const type = isSingle ? 'step' : 'section';

  const pasteLabel = `Paste ${Array.isArray(clipboardDataContent?.data) ? 'section' : 'step'}`;

  const [copyLabel, setCopyLabel] = React.useState(`Copy link to ${type}`);

  const { copyToClipboard } = useClipboard({
    onCopy: () => {
      setCopyLabel('Copied');
      setTimeout(() => {
        clearContextMenuState();
      }, 1000);
    },
  });

  const onDeleteSection = () => {
    if (items && items?.length > 1) {
      items?.forEach((item) => {
        dispatchUserEditorAction({
          type: 'delete-section',
          id: item,
        });
        clearContextMenuState();
      });
    }

    dispatchUserEditorAction({
      type: 'delete-section',
      id: section.id,
    });
    clearContextMenuState();
  };

  const onRenameSection = () => {
    renameAction();
    clearContextMenuState();
    setCurrentSectionId(section.id);
  };

  const onHiddenSection = () => {
    setIsCurrentSectionHidden(!isCurrentSectionHidden);

    if (items && items?.length > 1) {
      return items?.forEach((item) =>
        dispatchUserEditorAction({
          type: 'hidden-section',
          id: item,
        })
      );
    }

    dispatchUserEditorAction({
      type: 'hidden-section',
      id: section.id,
    });

    return setSectionHidden(section.id, !isCurrentSectionHidden);
  };

  const onCopySection = async () => {
    if (items && items?.length > 1) {
      copyFolder(items);
      return clearContextMenuState();
    }

    await copySectionBySectionId(section.id);

    return clearContextMenuState();
  };

  const onPasteSection = () => {
    if (!enablePasteForSection || !contextMenuState || !clipboardData) {
      return;
    }
    if (contextMenuState.entity.type !== 'section-card' || clipboardData.type !== 'section') {
      return;
    }

    if (enablePasteForSection) {
      const { sectionId } = contextMenuState.entity;
      const sectionIndex = sectionIds.findIndex((s) => s === sectionId);
      if (sectionIndex === -1) {
        return;
      }
      dispatchUserEditorAction({
        type: 'paste-section',
        pasteSectionIndex: sectionIndex,
        clipboardData: clipboardData.data as Section[],
      });
    }
    clearContextMenuState();
  };

  const copyAliasToClipboard = () => {
    const currentSectionId = items && items[0];
    const friendlyPath = sections.find(({ id }) => id === currentSectionId)?.friendlyPath;

    let shareUrl = generateJourneyShareUrl(currentOrganization, journey, friendlyPath);

    if (!shareUrl.includes(friendlyPathFromProps)) {
      shareUrl = generateJourneyShareUrl(currentOrganization, journey, friendlyPathFromProps);
    }

    copyToClipboard(shareUrl);
  };

  return (
    <>
      {isCurrentSectionHidden ? (
        <ContextMenuButton onClick={onHiddenSection} label='Show'>
          <PreviewRegularIcon className='w-4 h-4' />
        </ContextMenuButton>
      ) : (
        <ContextMenuButton onClick={onHiddenSection} label='Hide'>
          <HiddenRegularIcon className='w-4 h-4' />
        </ContextMenuButton>
      )}
      <ContextMenuButton onClick={onRenameSection} label='Rename'>
        <EditRegularIcon className='w-4 h-4' />
      </ContextMenuButton>

      <ContextMenuButton onClick={onCopySection} label={`Copy ${type}`}>
        <CopyRegularIcon className='w-4 h-4' />
      </ContextMenuButton>

      <ContextMenuButton onClick={onPasteSection} label={pasteLabel} disabled={!enablePasteForSection}>
        <ContextPasteRegularIcon className='w-4 h-4' />
      </ContextMenuButton>

      <ContextMenuButton onClick={onDeleteSection} label={`Delete ${type}`}>
        <DeleteRegularIcon className='w-4 h-4' />
      </ContextMenuButton>

      <ContextMenuButton onClick={copyAliasToClipboard} label={copyLabel}>
        <LinkRegularIcon className='w-4 h-4' />
      </ContextMenuButton>
    </>
  );
};
